import { useState } from "react";
import { useAdminData } from "../state/AdminDataContext";
import { Field, Modal } from "./Modal";

/**
 * The Monthly/Quarterly/Annual rows every plan's price is computed against
 * (base_price * cycle.multiplier, the months paid for — see owner-portal's
 * BillingTab.tsx). Previously these 3 rows had no admin UI at all — the
 * discount baked into quarterly/annual pricing could only ever be changed
 * by hand in the database.
 */
export function BillingCyclesPanel() {
  const { cycles, cycleSave } = useAdminData();
  const [editing, setEditing] = useState<{ id: string; label: string; months: number; mult: number } | null>(null);

  return (
    <section className="o-card" style={{ margin: 0 }}>
      <div className="o-card__top">
        <div style={{ flex: 1 }}>
          <h2>Billing cycles</h2>
          <div className="o-card__sub">The default discount vs. paying monthly. A plan can set its own price.</div>
        </div>
      </div>
      <div className="o-table o-table--fit" style={{ ["--cols" as string]: "1.2fr 1fr 1fr 44px" }}>
        <div className="o-table__head">
          <span>Label</span>
          <span>Months</span>
          <span>Discount</span>
          <span />
        </div>
        {cycles.map((c) => (
          <div key={c.id} className="o-table__row" style={{ cursor: "default" }}>
            <span style={{ fontWeight: 800 }}>{c.label}</span>
            <span className="num">{c.months}</span>
            {c.months === 1 ? (
              <span className="num">0%</span>
            ) : (
              <input
                className="o-input num"
                aria-label={`${c.label} discount, percent`}
                key={c.mult}
                defaultValue={Math.round((1 - c.mult / c.months) * 100)}
                inputMode="numeric"
                style={{ width: 72, height: 36 }}
                onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                onBlur={(e) => {
                  const pct = Math.min(99, Math.max(0, Number(e.target.value) || 0));
                  if (pct !== Math.round((1 - c.mult / c.months) * 100)) cycleSave(c.id, { label: c.label, months: c.months, multiplier: c.months * (1 - pct / 100) });
                }}
              />
            )}
            <button type="button" className="o-icon-btn" aria-label={`Edit ${c.label}`} onClick={() => setEditing({ id: c.id, label: c.label, months: c.months, mult: c.mult })}>
              <span className="ms" aria-hidden="true">edit</span>
            </button>
          </div>
        ))}
      </div>

      {editing && (
        <CycleModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSubmit={(form) => {
            cycleSave(editing.id, form);
            setEditing(null);
          }}
        />
      )}
    </section>
  );
}

function CycleModal({
  initial,
  onClose,
  onSubmit,
}: {
  initial: { label: string; months: number; mult: number };
  onClose: () => void;
  onSubmit: (form: { label: string; months: number; multiplier: number }) => void;
}) {
  const [label, setLabel] = useState(initial.label);
  const [months, setMonths] = useState(String(initial.months));
  const [savePct, setSavePct] = useState(String(Math.round((1 - initial.mult / initial.months) * 100)));

  const submit = () => {
    const monthsNum = Math.max(1, Math.round(Number(months) || 1));
    const pct = Math.min(99, Math.max(0, Number(savePct) || 0));
    const multiplier = monthsNum * (1 - pct / 100);
    onSubmit({ label, months: monthsNum, multiplier });
  };

  return (
    <Modal
      onClose={onClose}
      width={500}
      title="Edit billing cycle"
      sub={initial.label}
      footer={
        <>
          <button type="button" onClick={onClose} className="o-btn o-btn--grey">
            Cancel
          </button>
          <button type="button" onClick={submit} className="o-btn o-btn--ink">
            <span className="ms" aria-hidden="true">check</span>
            Save cycle
          </button>
        </>
      }
    >
      <Field label="Label">
        <input className="o-input" aria-label="Label" value={label} onChange={(e) => setLabel(e.target.value)} />
      </Field>
      <div className="o-fields2">
        <Field label="Months">
          <input className="o-input num" aria-label="Months" value={months} onChange={(e) => setMonths(e.target.value)} inputMode="numeric" />
        </Field>
        <Field label="Discount vs. monthly (%)">
          <input className="o-input num" aria-label="Discount vs. monthly" value={savePct} onChange={(e) => setSavePct(e.target.value)} inputMode="numeric" placeholder="0" />
        </Field>
      </div>
      <div className="o-note o-note--info" style={{ marginTop: 0 }}>
        <span className="ms" aria-hidden="true">info</span>
        <span>
          A plan priced at 100,000 a month bills{" "}
          {(Math.max(1, Math.round(Number(months) || 1)) * (1 - Math.min(99, Math.max(0, Number(savePct) || 0)) / 100) * 100000).toLocaleString("en-US")} MMK for this cycle.
        </span>
      </div>
    </Modal>
  );
}
